import * as React from 'react';
import { BodyPrimary, H1, H3 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { CategoryLink } from '@/types/CategoryLink';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getSearchUrlWithParams, getSellerUrl, getSimilarItemsUrl } from '@/utils/urls';
import { parseQueryParams } from '@/utils/queryParams';
import { safelyEscapeText } from '@/utils/safelyRender';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { Suggestions } from '@/types/SimilarItemInfo';
import { useLocation } from 'react-router-dom';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import PriceResults from '@/components/PriceResults/PriceResults';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import SimilarItemsContainer from '@/components/SimilarItems/SimilarItemsContainer';
import styled, { css } from 'styled-components';

interface QueryParams {
    filterTerm: string;
}

const generateSuggestionLinks = (suggestion: any, house: any) => {
    let suggestionLink;
    if (suggestion) {
        suggestionLink = (
            <>
                “
                <Link
                    key="suggestion-link"
                    route
                    to={getSimilarItemsUrl(suggestion.value)}
                >
                    {suggestion.label}
                </Link>
                ”
            </>
        );
    }

    let houseLink;
    if (house) {
        houseLink = (
            <Link
                key="house-link"
                route
                to={getSellerUrl(house.houseId, house.value)}
            >
                {safelyEscapeText(house.value)}
            </Link>
        );
    }

    if (suggestionLink && houseLink) {
        return (
            <FormattedMessage
                id="searchResults.or"
                values={{ houseLink, suggestionLink }}
            />
        );
    }

    return suggestionLink || houseLink;
};

export type ZeroResultsProps = {
    allSearchParams: SearchParamsType;
    categoryLinks: CategoryLink[];
    categoryPage?: boolean;
    className?: string;
    creator?: any;
    houses: any[];
    isLoggedIn: boolean;
    saveSearchParams: any;
    searchTerm: string;
    seeResultsRoute?: string;
    soldItemIds?: number[];
    soldItemsFound?: number;
    suggestions: Suggestions[];
};

const ZeroResults = ({
    allSearchParams,
    categoryLinks,
    categoryPage,
    className = '',
    creator,
    houses,
    isLoggedIn,
    saveSearchParams,
    searchTerm,
    seeResultsRoute = '',
    soldItemIds,
    soldItemsFound,
    suggestions,
}: ZeroResultsProps) => {
    const { search } = useLocation();
    const { filterTerm }: QueryParams = parseQueryParams(search);

    if (!searchTerm) {
        return null;
    }
    const itemId = soldItemIds?.[0];

    const firstSuggestion = suggestions?.[0];
    const firstHouse = houses?.[0];
    const hasSuggestions = Boolean(firstSuggestion) || Boolean(firstHouse);
    const isCreatorPage = Boolean(creator?.name);
    const modifiedSearchTerm = categoryPage ? searchTerm : `“${searchTerm}”`;

    return (
        <StyledZeroResults className={className}>
            <div>
                <H1 className="mb-lg">
                    {categoryPage ? (
                        <>
                            {searchTerm}
                            {filterTerm && <> - "{filterTerm}"</>}
                            <StyledResultsCount>
                                (
                                <FormattedMessage
                                    id="search_item_count_items"
                                    values={{ count: 0 }}
                                />
                                )
                            </StyledResultsCount>
                        </>
                    ) : (
                        <FormattedMessage
                            id="searchResults.noResultsTitle"
                            values={{
                                searchTerm,
                            }}
                        />
                    )}
                </H1>
            </div>
            {hasSuggestions && !categoryPage && (
                <StyledSuggestion>
                    <FormattedMessage
                        id="searchResults.lookingFor"
                        values={{
                            suggestion: generateSuggestionLinks(firstSuggestion, firstHouse),
                        }}
                    />
                </StyledSuggestion>
            )}
            <StyledTop $categoryPage={categoryPage}>
                <ZeroResultsInformation>
                    <NoResults>
                        <H3>
                            <FormattedMessage id="searchResults.noResultsSubtitle" />
                        </H3>
                    </NoResults>
                    <FollowSearchMessage>
                        <BodyPrimary>
                            <FormattedMessage
                                id="searchResults.noResultsSubSubtitle1"
                                values={{
                                    count: (
                                        <FormattedNumber
                                            key="zero-results-count"
                                            maximumFractionDigits={0}
                                            minimumFractionDigits={0}
                                            value={soldItemsFound}
                                        />
                                    ),
                                    searchTerm: <StyledSubtitleTerm>{modifiedSearchTerm}</StyledSubtitleTerm>,
                                }}
                            />
                            <br />
                            <span>
                                <FormattedMessage id="searchResults.noResultsSubSubtitle2" />
                            </span>
                        </BodyPrimary>
                    </FollowSearchMessage>
                    <UserActions>
                        <SaveButtonContainer>
                            <SaveSearchContainer
                                allSearchParams={allSearchParams}
                                saveSearchParams={saveSearchParams}
                                small
                            />
                        </SaveButtonContainer>
                        {soldItemsFound > 0 && (
                            <PriceResultsContainer>
                                <PriceResults
                                    count={soldItemsFound || 0}
                                    creator={creator?.name && searchTerm}
                                    isLoggedIn={isLoggedIn}
                                    seeAllUrl={
                                        isCreatorPage
                                            ? seeResultsRoute
                                            : getSearchUrlWithParams({
                                                  keyword: searchTerm,
                                                  page: 1,
                                                  pageSize: 24,
                                                  sort: SearchSortAndDirection.BestMatch,
                                                  status: 'archive',
                                              })
                                    }
                                />
                            </PriceResultsContainer>
                        )}
                    </UserActions>
                </ZeroResultsInformation>
            </StyledTop>
            <SimilarItems>
                {itemId && (
                    <SimilarItemsContainer
                        categoryLinks={categoryLinks}
                        itemId={itemId}
                        suggestions={suggestions}
                        title={
                            <FormattedMessage
                                id="searchResults.relatedTo"
                                values={{ searchTerm: modifiedSearchTerm }}
                            />
                        }
                    />
                )}
            </SimilarItems>
        </StyledZeroResults>
    );
};

export default React.memo<ZeroResultsProps>(ZeroResults, isEqual);

const StyledZeroResults = styled.div`
    display: block;
    max-width: 1200px;
`;

const StyledResultsCount = styled.span`
    color: ${({ theme }) => theme.colors.grey300};
    font-size: 14px;
    letter-spacing: 0.5px;
    padding-right: 6px;
    line-height: 25px;
    margin-left: 8px;
`;

const StyledSuggestion = styled.div`
    margin-bottom: 30px;
`;

const StyledTop = styled.div<{ $categoryPage: boolean }>`
    display: flex;
    flex-direction: row;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    justify-content: space-between;
    padding: 32px;
    ${({ $categoryPage }) =>
        $categoryPage
            ? css`
                  margin-top: 20px;
              `
            : ''};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: column;
        margin-bottom: 31px;
    }
`;

const ZeroResultsInformation = styled.section`
    flex: 1 1 auto;
    max-width: 643px;
    margin-right: 30px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 24px;
        margin-right: 0;
        max-width: unset;
        text-align: center;
    }
`;

const NoResults = styled.div`
    margin-bottom: 8px;
`;

const FollowSearchMessage = styled.div`
    margin-bottom: 25px;

    span {
        color: ${({ theme }) => theme.colors.grey100};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 20px;
    }
`;

const StyledSubtitleTerm = styled.span`
    font-style: italic;
`;

const UserActions = styled.div`
    display: flex;
`;

const SaveButtonContainer = styled.div`
    width: 249px;
    button {
        width: 100%;
    }
`;

const PriceResultsContainer = styled.div`
    margin: auto 0 auto 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-left: 0;
    }
`;

const SimilarItems = styled.div`
    margin-top: 128px;
`;
